import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash, LockKey, Eye, EyeSlash, Power, ArrowsClockwise } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';


function UserCard({ user, onRefresh }) {
  const [showPassword, setShowPassword] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [acting, setActing] = useState(false);

  const getRoleBadge = (role) => {
    const roleMap = {
      admin: 'bg-red-50 text-red-700 border-red-200',
      executive: 'bg-blue-50 text-blue-700 border-blue-200',
      procurement: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      production: 'bg-purple-50 text-purple-700 border-purple-200',
      installation: 'bg-orange-50 text-orange-700 border-orange-200',
      sales: 'bg-teal-50 text-teal-700 border-teal-200',
      inventory: 'bg-amber-50 text-amber-700 border-amber-200'
    };
    return <Badge className={`${roleMap[role] || 'bg-zinc-50 text-zinc-700'} border capitalize`}>{role}</Badge>;
  };

  const handleToggleStatus = async () => {
    setActing(true);
    try {
      const { data } = await api.put(`/api/users/${user.user_id}/status`, {});
      toast.success(data.message);
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update status');
    } finally {
      setActing(false);
    }
  };

  const handleForcePasswordChange = async () => {
    setActing(true);
    try {
      const { data } = await api.put(`/api/users/${user.user_id}/force-password-change`, {});
      toast.success(data.message);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update force password change');
    } finally {
      setActing(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setActing(true);
    try {
      const { data } = await api.post(`/api/users/${user.user_id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(data.message);
      setResetDialog(false);
      setNewPassword('');
      onRefresh();
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setActing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete user ${user.user_id}?`)) return;
    try {
      await api.delete(`/api/users/${user.user_id}`);
      toast.success('User deleted successfully');
      onRefresh();
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const isActive = user.is_active !== false;

  return (
    <>
      <Card data-testid={`user-card-${user.user_id}`} className={`border-zinc-300 rounded-sm shadow-sm ${!isActive ? 'opacity-60 bg-zinc-50' : ''}`}>
        <CardContent className="p-5">
          {/* Header: Name + Role + Status */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-zinc-950 truncate">{user.name}</h3>
              <p className="text-sm text-zinc-500 mt-0.5">ID: {user.user_id}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {getRoleBadge(user.role)}
              <Badge
                data-testid={`user-status-${user.user_id}`}
                className={`border ${isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
              >
                {isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>

          {/* Password Display */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 mb-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">Password</span>
                <p data-testid={`user-password-${user.user_id}`} className="text-sm font-mono mt-0.5 text-zinc-700">
                  {showPassword ? (user.password_plain || '(not stored)') : '••••••••'}
                </p>
              </div>
              <Button
                data-testid={`toggle-password-${user.user_id}`}
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                className="text-zinc-500 hover:text-zinc-700"
              >
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </Button>
            </div>
          </div>

          {/* Force Password Change indicator */}
          {user.force_password_change && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-3 text-xs font-bold text-amber-700">
              Force password change on next login
            </div>
          )}

          {/* Action Buttons */}
          {user.role !== 'admin' && (
            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid={`reset-password-${user.user_id}`}
                variant="outline"
                size="sm"
                onClick={() => setResetDialog(true)}
                disabled={acting}
                className="border-blue-300 text-blue-700 hover:bg-blue-50 h-9 text-xs font-bold"
              >
                <LockKey size={14} className="mr-1" />
                Set Password
              </Button>
              <Button
                data-testid={`toggle-status-${user.user_id}`}
                variant="outline"
                size="sm"
                onClick={handleToggleStatus}
                disabled={acting}
                className={`h-9 text-xs font-bold ${isActive ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
              >
                <Power size={14} className="mr-1" />
                {isActive ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                data-testid={`force-change-${user.user_id}`}
                variant="outline"
                size="sm"
                onClick={handleForcePasswordChange}
                disabled={acting}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 h-9 text-xs font-bold"
              >
                <ArrowsClockwise size={14} className="mr-1" />
                {user.force_password_change ? 'Remove Force' : 'Force Change'}
              </Button>
              <Button
                data-testid={`delete-user-${user.user_id}`}
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="border-red-300 text-red-700 hover:bg-red-50 h-9 text-xs font-bold"
              >
                <Trash size={14} className="mr-1" />
                Delete
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set New Password for {user.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 pt-4">
            <p className="text-sm text-zinc-500">User ID: <strong>{user.user_id}</strong></p>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">New Password *</Label>
              <Input
                data-testid={`reset-pw-input-${user.user_id}`}
                type="text"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="h-11"
              />
            </div>
            <p className="text-xs text-amber-600">User will be required to change password on next login.</p>
            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => { setResetDialog(false); setNewPassword(''); }}>Cancel</Button>
              <Button type="submit" data-testid={`reset-pw-submit-${user.user_id}`} disabled={acting} className="bg-blue-600 hover:bg-blue-700 text-white">
                {acting ? 'Setting...' : 'Set Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [resetRequests, setResetRequests] = useState([]);
  const [formData, setFormData] = useState({
    user_id: '',
    password: '',
    name: '',
    role: 'executive'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/users`);
      setUsers(data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchResetRequests = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/password-reset-requests`);
      setResetRequests(data.filter(r => r.status === 'pending'));
    } catch {
      setResetRequests([]);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchResetRequests();
  }, [fetchUsers, fetchResetRequests]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/users`, formData);
      toast.success('User created successfully');
      setShowDialog(false);
      setFormData({ user_id: '', password: '', name: '', role: 'executive' });
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create user');
    }
  };

  const handleRefresh = () => {
    fetchUsers();
    fetchResetRequests();
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">User Management</h1>
            <p className="text-sm text-zinc-500 mt-2">Manage users, passwords, and access controls</p>
          </div>
          <Button
            data-testid="create-user-button"
            onClick={() => setShowDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Create User
          </Button>
        </div>

        {/* Password Reset Requests Banner */}
        {resetRequests.length > 0 && (
          <div data-testid="password-reset-requests-banner" className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <h3 className="text-sm font-bold text-amber-800 mb-2">Pending Password Reset Requests ({resetRequests.length})</h3>
            <div className="space-y-2">
              {resetRequests.map((req) => (
                <div key={`${req.user_id}_${req.requested_at}`} className="flex items-center justify-between bg-white border border-amber-100 rounded-sm px-3 py-2">
                  <div>
                    <span className="text-sm font-bold text-zinc-700">{req.user_name}</span>
                    <span className="text-xs text-zinc-500 ml-2">({req.user_id}) - {req.role}</span>
                    <span className="text-xs text-zinc-400 ml-2">{req.reason}</span>
                  </div>
                  <span className="text-xs text-amber-600">{new Date(req.requested_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-600 mt-2">Use "Set Password" on the user card below to resolve these requests.</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">Loading users...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {users.map((user) => (
              <UserCard key={user.user_id} user={user} onRefresh={handleRefresh} />
            ))}
          </div>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">User ID *</Label>
              <Input
                data-testid="new-user-id-input"
                required
                value={formData.user_id}
                onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Password *</Label>
              <Input
                data-testid="new-user-password-input"
                type="text"
                required
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Name *</Label>
              <Input
                data-testid="new-user-name-input"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Role *</Label>
              <Select value={formData.role} onValueChange={(val) => setFormData(prev => ({ ...prev, role: val }))}>
                <SelectTrigger data-testid="new-user-role-select" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="executive">Executive</SelectItem>
                  <SelectItem value="procurement">Procurement</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                  <SelectItem value="installation">Installation Team</SelectItem>
                  <SelectItem value="sales">Sales Team</SelectItem>
                  <SelectItem value="inventory">Inventory Management</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-create-user-button"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Create User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
